/**
 * AdminNetworkMap — CaraLoom's National Network Map (iter 179).
 *
 * Live US tile-grid of every VERIFIED provider in the directory, colored
 * by coverage tier so BD/recruiting can spot gaps at a glance.
 *
 * Route: /admin/network-map (admin + provider org roles)
 * Testids:
 *   - admin-network-map-page
 *   - network-map-filter-<slug>            (level of care chip)
 *   - network-map-tile-<STATE>             (each state square)
 *   - network-map-gap-row-<STATE>          (each row in the recruiting table)
 *   - network-map-kpi-{total|states|median|gaps}
 *
 * Data sources: /api/admin/providers/network-map (aggregates over
 * `provider_directory` seeded by the CMS Care Compare importer).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Map as MapIcon, Loader2, RefreshCw, TrendingDown, Layers, Building2,
  Users, Inbox, Shield, ShieldCheck, Sparkles,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_NAV = [
  { icon: Shield,      label: 'Compliance',       href: '/admin/compliance' },
  { icon: ShieldCheck, label: 'AI Validation',    href: '/admin/ai-validation' },
  { icon: Inbox,       label: 'Intake Inbox',     href: '/admin/intake' },
  { icon: Building2,   label: 'Referral Sources', href: '/admin/referral-sources' },
  { icon: Users,       label: 'CRM',              href: '/admin/crm' },
  { icon: ShieldCheck, label: 'HIE Console',      href: '/admin/hie' },
  { icon: MapIcon,     label: 'Network Map',      href: '/admin/network-map', active: true },
];

const LEVELS = [
  { slug: '',                 label: 'All post-acute' },
  { slug: 'skilled-nursing',  label: 'SNF' },
  { slug: 'home-health',      label: 'Home Health' },
  { slug: 'hospice',          label: 'Hospice' },
  { slug: 'irf',              label: 'IRF' },
  { slug: 'ltach',            label: 'LTACH' },
  { slug: 'assisted-living',  label: 'Assisted Living' },
  { slug: 'memory-care',      label: 'Memory Care' },
  { slug: 'palliative',       label: 'Palliative' },
  { slug: 'dme',              label: 'DME' },
];

// Canonical US state tile-grid (NPR-style, 8 rows × 12 cols). `null` = empty
// cell. Weighs every state equally so coverage gaps in low-population states
// aren't visually swamped by geographically-large ones — the whole point of
// this view.
const TILE_GRID = [
  ['AK', null, null, null, null, null, null, null, null, null, null, 'ME'],
  [null, null, null, null, null, null, null, null, null, null, 'VT', 'NH'],
  ['WA', 'ID', 'MT', 'ND', 'MN', null, null, null, null, 'MI', 'NY', 'MA'],
  ['OR', 'NV', 'WY', 'SD', 'IA', 'WI', 'IL', 'IN', 'OH', 'PA', 'NJ', 'CT'],
  ['CA', 'UT', 'CO', 'NE', 'MO', 'KY', 'WV', 'VA', 'MD', 'DC', 'DE', 'RI'],
  [null, 'AZ', 'NM', 'KS', 'AR', 'TN', 'NC', 'SC', null, null, null, null],
  [null, null, null, 'OK', 'LA', 'MS', 'AL', 'GA', null, null, null, null],
  ['HI', null, null, 'TX', null, null, null, 'FL', null, null, null, null],
];

// Tier → tile fill classes. Chosen so the map reads at a glance: red = act
// now, green = healthy. Text stays high-contrast (slate-900) on all tiers
// so counts stay legible.
const TIER_STYLE = {
  critical: { fill: '#7f1d1d', stroke: '#fca5a5', text: '#fecaca', label: 'No coverage' },
  gap:      { fill: '#c2410c', stroke: '#fdba74', text: '#fed7aa', label: 'Recruiting gap' },
  thin:     { fill: '#a16207', stroke: '#fcd34d', text: '#fef3c7', label: 'Thin' },
  strong:   { fill: '#166534', stroke: '#86efac', text: '#dcfce7', label: 'Healthy' },
  empty:    { fill: '#0f172a', stroke: '#1e293b', text: '#475569', label: '—' },
};

function fmtN(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString();
}

function Kpi({ icon: Icon, label, value, testId, tone = 'default' }) {
  const toneCls = {
    default: 'text-cn-text',
    warn:    'text-amber-600 dark:text-amber-300',
    danger:  'text-rose-600 dark:text-rose-300',
  }[tone] || 'text-cn-text';
  return (
    <div className="rounded-lg border border-cn-border bg-cn-bg dark:bg-white/[0.03] px-3 py-2" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-cn-text-muted">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`text-xl font-semibold tabular-nums mt-0.5 ${toneCls}`}>{value ?? 0}</div>
    </div>
  );
}

// SVG tile-grid US map. Each cell is 60×48 with the 2-letter code on top
// and the count below.
function TileMap({ statesByCode, onHover }) {
  const CELL_W = 60;
  const CELL_H = 48;
  const GAP = 4;
  const cols = TILE_GRID[0].length;
  const rows = TILE_GRID.length;
  const width = cols * (CELL_W + GAP) - GAP;
  const height = rows * (CELL_H + GAP) - GAP;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full h-auto max-h-[520px]"
      role="img"
      aria-label="US National Network Map"
    >
      {TILE_GRID.map((row, ri) =>
        row.map((code, ci) => {
          if (!code) return null;
          const rec = statesByCode.get(code);
          const tier = rec?.tier || 'empty';
          const style = TIER_STYLE[tier];
          const x = ci * (CELL_W + GAP);
          const y = ri * (CELL_H + GAP);
          return (
            <g
              key={code}
              transform={`translate(${x}, ${y})`}
              onMouseEnter={() => onHover?.(rec || { code, name: code, count: 0, tier: 'empty' })}
              onMouseLeave={() => onHover?.(null)}
              data-testid={`network-map-tile-${code}`}
              className="cursor-pointer transition-opacity hover:opacity-90"
            >
              <rect
                width={CELL_W}
                height={CELL_H}
                rx={6}
                fill={style.fill}
                stroke={style.stroke}
                strokeWidth={1}
              />
              <text
                x={CELL_W / 2}
                y={CELL_H / 2 - 4}
                textAnchor="middle"
                fill={style.text}
                fontSize="14"
                fontWeight="600"
                fontFamily="ui-sans-serif, system-ui, sans-serif"
              >
                {code}
              </text>
              <text
                x={CELL_W / 2}
                y={CELL_H / 2 + 12}
                textAnchor="middle"
                fill={style.text}
                fontSize="11"
                fontFamily="ui-sans-serif, system-ui, sans-serif"
                opacity="0.9"
              >
                {rec ? rec.count.toLocaleString() : '—'}
              </text>
            </g>
          );
        })
      )}
    </svg>
  );
}

function LegendChip({ tier }) {
  const s = TIER_STYLE[tier];
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-cn-text">
      <span
        className="inline-block h-3 w-3 rounded-sm border"
        style={{ backgroundColor: s.fill, borderColor: s.stroke }}
      />
      {s.label}
    </span>
  );
}

export default function AdminNetworkMap() {
  const { getAuthHeader } = useAuth();
  const [levelSlug, setLevelSlug] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await axios.get(`${API}/admin/providers/network-map`, {
        ...getAuthHeader(),
        params: levelSlug ? { level_of_care: levelSlug } : {},
      });
      setData(resp);
    } catch (e) {
      toast.error('Could not load the network map. Check your admin role.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, levelSlug]);

  useEffect(() => { load(); }, [load]);

  const statesByCode = useMemo(() => {
    const m = new Map();
    (data?.states || []).forEach((s) => m.set(s.code, s));
    return m;
  }, [data]);

  const gapCount = (data?.states || []).filter(
    (s) => s.tier === 'critical' || s.tier === 'gap'
  ).length;

  return (
    <DashboardShell navItems={ADMIN_NAV}>
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="admin-network-map-page">
        <header className="flex flex-wrap items-end gap-3 justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-cn-text tracking-tight flex items-center gap-2">
              <MapIcon className="h-5 w-5 text-cn-primary" /> National Network Map
            </h1>
            <p className="text-sm text-cn-text-muted mt-1 max-w-2xl">
              Every verified post-acute provider in CaraLoom&apos;s directory, plotted by state.
              Sourced from CMS Care Compare imports. Coverage tiers are computed from the
              national distribution — red states are the recruiting targets we should hit next.
            </p>
          </div>
          <Button onClick={load} variant="outline" size="sm" data-testid="network-map-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </header>

        <section className="flex flex-wrap items-center gap-2" data-testid="network-map-filter-row">
          <span className="text-xs uppercase tracking-wider text-cn-text-muted mr-1">
            <Layers className="inline h-3 w-3 mr-1" /> Level of care
          </span>
          {LEVELS.map((l) => {
            const active = levelSlug === l.slug;
            return (
              <button
                key={l.slug || 'all'}
                onClick={() => setLevelSlug(l.slug)}
                data-testid={`network-map-filter-${l.slug || 'all'}`}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                  active
                    ? 'bg-cn-primary/20 border-cn-primary/50 text-cn-primary'
                    : 'bg-cn-bone-100/60 dark:bg-white/[0.03] border-cn-border text-cn-text-muted hover:text-cn-text hover:border-cn-border/80'
                }`}
              >
                {l.label}
              </button>
            );
          })}
        </section>

        <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={Building2}   label="Verified providers" value={fmtN(data?.total_providers)} testId="network-map-kpi-total" />
          <Kpi icon={MapIcon}         label="States covered"     value={`${data?.states_covered ?? 0} / 51`} testId="network-map-kpi-states" />
          <Kpi icon={Sparkles}    label="Median per state"   value={fmtN(data?.median_per_state)} testId="network-map-kpi-median" />
          <Kpi icon={TrendingDown} label="Recruiting gaps"    value={gapCount} tone={gapCount > 0 ? 'warn' : 'default'} testId="network-map-kpi-gaps" />
        </section>

        <section className="rounded-2xl border border-cn-border bg-cn-bone-100/60 dark:bg-white/[0.03] p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-cn-text flex items-center gap-2">
              <MapIcon className="h-4 w-4 text-cn-text-muted" />
              {data?.level_of_care_label || 'All post-acute'} · coverage tiles
            </h2>
            <div className="flex flex-wrap gap-3" data-testid="network-map-legend">
              <LegendChip tier="critical" />
              <LegendChip tier="gap" />
              <LegendChip tier="thin" />
              <LegendChip tier="strong" />
            </div>
          </div>
          {loading ? (
            <div className="py-16 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-cn-text-muted" />
            </div>
          ) : !data ? (
            <div className="py-16 text-center text-sm text-cn-text-muted" data-testid="network-map-error">
              Couldn&apos;t load the map. Try refresh.
            </div>
          ) : (
            <>
              <TileMap statesByCode={statesByCode} onHover={setHovered} />
              <div
                className="mt-3 text-xs text-cn-text-muted h-5"
                data-testid="network-map-hover-detail"
              >
                {hovered ? (
                  <>
                    <span className="text-cn-text font-medium">{hovered.name}</span>
                    {' · '}
                    <span className="tabular-nums">{fmtN(hovered.count)}</span> verified providers
                    {' · '}
                    <span className="text-cn-text-muted">tier: {hovered.tier}</span>
                  </>
                ) : (
                  <span className="text-cn-text-muted">Hover a state to see details.</span>
                )}
              </div>
            </>
          )}
        </section>

        <section className="rounded-2xl border border-cn-border bg-cn-bone-100/60 dark:bg-white/[0.03] overflow-hidden">
          <div className="p-4 border-b border-cn-border flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-amber-400" />
            <h2 className="text-sm font-semibold text-cn-text">
              Top recruiting priorities
            </h2>
            <span className="text-xs text-cn-text-muted">
              — worst-covered states first (goal: bring them off the red list)
            </span>
          </div>
          {loading ? (
            <div className="p-10 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-cn-text-muted" />
            </div>
          ) : (data?.top_gaps || []).length === 0 ? (
            <div className="p-10 text-center text-sm text-cn-text-muted" data-testid="network-map-gaps-empty">
              No coverage gaps for this level of care — you&apos;re at healthy coverage
              across every state. 🟢
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-cn-text-muted border-b border-cn-border">
                  <th className="px-4 py-2 font-medium">State</th>
                  <th className="px-4 py-2 font-medium text-right">Providers</th>
                  <th className="px-4 py-2 font-medium">Tier</th>
                  <th className="px-4 py-2 font-medium">Suggested next step</th>
                </tr>
              </thead>
              <tbody>
                {(data?.top_gaps || []).map((s) => {
                  const style = TIER_STYLE[s.tier];
                  const next = s.tier === 'critical'
                    ? 'Book 3 CMS-verified operators this week; queue a solutions call.'
                    : 'Prioritize BD outreach — one anchor operator flips this to Thin.';
                  return (
                    <tr
                      key={s.code}
                      className="border-b border-cn-border/60 hover:bg-cn-bone-100 dark:hover:bg-white/[0.04]"
                      data-testid={`network-map-gap-row-${s.code}`}
                    >
                      <td className="px-4 py-3 font-medium text-cn-text">
                        {s.name} <span className="text-xs text-cn-text-muted">({s.code})</span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-cn-text">
                        {fmtN(s.count)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-medium"
                          style={{
                            backgroundColor: style.fill + '40',
                            borderColor: style.stroke + '80',
                            color: style.text,
                          }}
                        >
                          {style.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-cn-text-muted">{next}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>

        <p className="text-[11px] text-cn-text-muted italic">
          Coverage tiers are derived from the current national distribution:
          critical = 0 providers, gap ≤ {data?.tier_thresholds?.gap_max ?? '—'}, thin ≤ {data?.tier_thresholds?.thin_max ?? '—'}, otherwise healthy.
          Median per state is computed over states with any coverage so the tiers stay
          stable as levels of care mature. Only VERIFIED providers count.
        </p>
      </div>
    </DashboardShell>
  );
}
